import Configuration from '@/agilis/util/Configuration'
import ConfigurationDashboard from '@/agilis/util/ConfigurationDashboard'
import DashboardWrapper from '@/agilis/components/DashboardTabs/DashboardWrapper/DashboardWrapper.vue'
import FileUploader from '@/agilis/components/FileUploader/FileUploader.vue'
import FileUploaderV2 from '@/agilis/components/FileUploaderV2/FileUploaderV2.vue'
import TrackerDataTableItemActions from '@/applications/scrum/components/TrackerDataTable/TrackerDataTableItemActions/TrackerDataTableItemActions.vue'
import TrackerDataTableItemAssociations from '@/applications/scrum/components/TrackerDataTable/TrackerDataTableItemAssociations/TrackerDataTableItemAssociations.vue'
import TrackerDataTableItemChildren from '@/applications/scrum/components/TrackerDataTable/TrackerDataTableItemChildren/TrackerDataTableItemChildren.vue'
import TrackerDataTableItemCollaborators from '@/applications/scrum/components/TrackerDataTable/TrackerDataTableItemCollaborators/TrackerDataTableItemCollaborators.vue'
import TrackerDataTableItemNotes from '@/applications/scrum/components/TrackerDataTable/TrackerDataTableItemNotes/TrackerDataTableItemNotes.vue'
import TrackerDataTableItemTitle from '@/applications/scrum/components/TrackerDataTable/TrackerDataTableItemTitle/TrackerDataTableItemTitle.vue'
import TrackerHistory from '@/applications/scrum/components/TrackerHistory/TrackerHistory.vue'
import TrackerParent from '@/applications/scrum/components/TrackerParent/TrackerParent.vue'
import EntityDetails from '@/agilis/components/EntityDetails/EntityDetails.vue'

import toursMixin from '@/agilis/components/Tour/mixins/toursMixin'
import deleteMixin from '@/agilis/components/DataTableV2/common/deleteMixin'

import Vue from 'vue'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'TrackerDataTableView',
  mixins: [toursMixin, deleteMixin],

  props: {
    configuration: Object,
    parentJlAssertionContext: {
      type: Object,
      default: () => ({})
    }
  },

  components: {
    DashboardWrapper,
    FileUploader,
    FileUploaderV2,
    TrackerDataTableItemActions,
    TrackerDataTableItemAssociations,
    TrackerDataTableItemChildren,
    TrackerDataTableItemCollaborators,
    TrackerHistory,
    TrackerDataTableItemNotes,
    EntityDetails,
    TrackerParent,
    TrackerDataTableItemTitle
  },

  data () {
    return {
      trackerDTO: {},
      parentTrackerDTO: null,
      children: null,
      associations: null
    }
  },

  mounted: function () {
    this.startLoader({ id: 'tracker-details' })
    this.fetchData(this.trackerId)
    this.fetchParentData(this.trackerId)
  },

  computed: {
    ...mapGetters({
      userId: 'userDataStore/userId',
      selectedContext: 'contextStore/selectedContext',
      applicationId: 'contextStore/applicationId',
      selectedAppConfig: 'contextStore/application',
      workspaceId: 'contextStore/workspaceId',
      trackerType: 'contextStore/trackerType',
      trackerTypeId: 'contextStore/trackerTypeId',
      trackerId: 'contextStore/trackerId',
      dataTable: 'contextStore/trackerDataTable',
      dataTableById: 'contextStore/trackerDataTableById',
      loading: 'loaderStore/loading'
    }),

    trackerType () {
      return this.selectedAppConfig.trackerTypes[this.trackerTypeId]
    },

    trackerTypeCanHaveChildren () {
      const childrenTypesArray = this.selectedAppConfig.trackerTypes[this.trackerTypeId].childrenTypes
      return childrenTypesArray === undefined || childrenTypesArray.length > 0
    },

    formFields () {
      const formColumns = this.configuration.columns

      const formElements = Object.entries(this.configuration.formElements || {}).reduce((acc, [key, value]) => {
        if (value.type === 'divider') {
          return {
            ...acc,
            [key]: value
          }
        }
        return acc
      }, {})

      return {
        ...formColumns,
        ...formElements
      }
    },

    maxFormColumns () {
      const formRows = this.configuration.formRows
      return formRows.reduce(function (result, element) {
        return result >= element.cols.length * 2 ? result : element.cols.length * 2
      }, 0)
    },

    selectedContextWithTrackerId () {
      return Object.assign({ trackerId: this.trackerId }, this.selectedContext)
    },

    actionArray () {
      return Configuration.state.getEventsFromState(this.trackerDTO)
    },

    parentTracker () {
      return this.parentTrackerDTO && !!this.configuration.parentDetails
    },

    parentTrackerFormRows () {
      return this.configuration?.parentDetails?.formRows
    },

    jlAssertionContext () {
      return {
        ...this.parentJlAssertionContext,
        tracker: this.trackerDTO,
        childTrackersDTO: this.children,
        associations: this.associations
      }
    },

    trackerForRendering () {
      return Configuration.datatable.mapItemDTO(this.dataTable, this.trackerDTO)
    },

    entityTypes () {
      return this.trackerType.entityTypes || {}
    }
  },

  methods: {
    ...mapActions({
      'notifyError': 'notificationStore/notifyError',
      'startLoader': 'loaderStore/startLoader',
      'stopAllLoaders': 'loaderStore/stopAllLoaders'
    }),

    getDashboardContext () {
      return ConfigurationDashboard.getTrackerDashboardContext(
        this.applicationId,
        this.workspaceId,
        this.trackerTypeId,
        this.trackerId
      )
    },

    userCanDo (operation) {
      return Configuration.auth.userCanDo(this.selectedContext, operation, this.trackerDTO, this.userId)
    },

    closeItem () {
      this.$router.agilis.viewTrackerTable()
    },

    handleBreadcrumbClick (breadcrumb) {
      this.$router.agilis.viewTracker({
        trackerId: breadcrumb.selectedContext.trackerId,
        trackerTypeId: breadcrumb.selectedContext.trackerTypeId
      })
    },

    handleChildTrackerClick (tracker) {
      this.$router.agilis.viewTracker({
        trackerId: this.trackerId,
        trackerTypeId: this.trackerTypeId
      })
    },

    handleNewChildTrackerClick (trackerId) {
      this.$emit('click-new-child-tracker', trackerId)
    },

    handleEditTracker (trackerId) {
      this.$router.agilis.editTracker({
        trackerId: trackerId
      })
    },

    handleChangeTrackerState (newStateId) {
      this.fetchData(this.trackerId)
      this.requestHistoryReload()
      this.requestChildrenReload()
      this.requestCollaboratorReload()
    },

    fetchData (trackerId) {
      const url = Vue.axios.agilisUrlTrackerEdit(this.applicationId, this.trackerTypeId, trackerId)
      Vue.axios
        .get(
          url,
          Vue.axios.agilisRequestConfig(url))
        .then((response) => {
          this.trackerDTO = JSON.parse(JSON.stringify(response.data))
        })
        .catch((error) => {
          this.notifyError(error)
        })
        .finally(() => {
          this.stopAllLoaders({ ids: ['tracker-details', 'change-tracker-state'] })
        })
    },

    async fetchParentData (trackerId) {
      const url = Vue.axios.agilisUrlTrackerParents(
        this.applicationId,
        this.trackerTypeId,
        this.trackerId)

      try {
        const parentsResponse = await Vue.axios.get(
          url,
          Vue.axios.agilisRequestConfig(url)
        )

        let parentTracker = parentsResponse.data?.parentTracker?.tracker
        if (parentTracker) {
          // ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
          // TODO: la chiamata di recupero dei parent NON TORNA TUTTE LE INFO! => facciamo una seconda chiamata sul tracker specifico
          // ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
          const urlParent = Vue.axios.agilisUrlTrackerEdit(this.applicationId, parentTracker.trackerTypeId, parentTracker.id)
          const parentTrackerGetResponse = await Vue.axios.get(
            urlParent,
            Vue.axios.agilisRequestConfig(urlParent)
          )
          parentTracker = parentTrackerGetResponse.data
          // ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

          this.parentTrackerDTO = parentTracker
        }
      } catch (error) {
        if (error?.response?.status !== 401) this.notifyError(error)
      }
    },

    userCanAccessField (colId) {
      return Configuration.auth.userCanAccessField(this.selectedContext, colId)
    },

    userCanAccessFeature (featureId) {
      return Configuration.auth.userCanAccessFeature(
        this.selectedContext, featureId, null, this.trackerDTO, this.userId)
    },

    requestHistoryReload () {
      if (this.$refs.history && typeof this.$refs.history.forceReload === 'function') {
        this.$refs.history.forceReload()
      }
    },

    requestChildrenReload () {
      if (this.$refs.children?.$refs?.componentResolved && typeof this.$refs.children.$refs.componentResolved.fetchChildren === 'function') {
        this.$refs.children.$refs.componentResolved.fetchChildren()
      }
    },

    requestCollaboratorReload () {
      if (this.$refs.collaborators && typeof this.$refs.collaborators.fetchCollaborators === 'function') {
        this.$refs.collaborators.fetchCollaborators()
      }
    },

    handleChildrenLoaded (children) {
      this.children = children
    },

    handleAssociationsLoaded (associations) {
      this.associations = associations
      this.$emit('associationsLoaded', associations)
    }
  }

}
